import { PAWN_VISION_RADIUS, TILE_SIZE } from '@dreamgame/shared';
import { Filter, GlProgram, Sprite, Texture } from 'pixi.js';
import { IS_SAFARI } from '../../platform/browser';
import { ownedPawns } from '../../state/entities';
import { fogVersion, isTileExplored, updateExploredTiles } from '../../state/fogOfWar';
import { getCamera, getScreenDimensions } from '../Camera';

// ============================================================================
// FogLayer - Screen-space fog of war overlay
// ============================================================================
// Renders fog as a screen-space sprite (on stage, not worldContainer).
// Uses an offscreen Canvas2D at multiple pixels per tile for smooth gradients.
// PixiJS linear texture filtering further smooths the result.
//
// Two-buffer interpolation:
//   - _targetAlpha: desired fog alpha per pixel (recomputed on state changes)
//   - _displayAlpha: what's currently rendered (lerps toward target each frame)
//   This gives smooth temporal transitions instead of instant fog pops.
//
// Three visibility states:
//   - Visible (in pawn vision): fully transparent (alpha 0)
//   - Explored (previously seen): semi-transparent dark overlay
//   - Unexplored (never seen): nearly opaque dark overlay
// ============================================================================

let sprite: Sprite;
let offscreenCanvas: HTMLCanvasElement;
let ctx: CanvasRenderingContext2D;
let _imageData: ImageData | null = null;
let _currentTexture: Texture | null = null;
let _fogEnabled = true;

// PERFORMANCE: Cache last drawn state to skip redundant target recomputation
let _lastFogVersion = -1;
let _lastCameraTileX = -99999;
let _lastCameraTileY = -99999;
let _lastZoom = -1;
let _lastScreenW = 0;
let _lastScreenH = 0;

// Two-buffer interpolation buffers (normalized 0..1 alpha)
let _targetAlpha: Float32Array = new Float32Array(0);
let _displayAlpha: Float32Array = new Float32Array(0);
// PERFORMANCE: scratch buffer for viewport shift remapping
let _remapTemp: Float32Array = new Float32Array(0);

// Previous viewport origin for display buffer remapping on camera pan
let _prevMinTileX = -99999;
let _prevMinTileY = -99999;
let _prevCanvasW = 0;
let _prevCanvasH = 0;

// Frame timing for frame-rate-independent interpolation
let _lastFrameTime = 0;

// When true, display matches target to within epsilon; skip per-pixel work
let _isConverged = true;

// Sub-tile resolution: pixels per tile on the fog canvas.
// PERFORMANCE: Adaptive based on zoom level. At low zoom, each tile covers fewer
// screen pixels so sub-tile fog detail is invisible. Reducing pixels per tile at
// low zoom cuts canvas area (and all CPU-side fog work) by 4-16x.
// Discrete breakpoints prevent constant buffer reallocation during smooth zooming.
let _fogPixelsPerTile = 4;
let _invFogPixelsPerTile = 1 / _fogPixelsPerTile;

// Margin in tiles beyond viewport for fog computation (includes hidden buffer)
const FOG_MARGIN_TILES = 6;

// Display inset: tiles of fog canvas hidden on each side as a transition buffer.
// New pixels from camera shifts appear in this hidden zone and interpolate smoothly
// before scrolling into the visible display area.
const FOG_DISPLAY_INSET_TILES = 2;
let _fogDisplayInsetPx = FOG_DISPLAY_INSET_TILES * _fogPixelsPerTile;

// PERFORMANCE: Compute fog resolution from zoom level.
// zoom >= 1.0: 4 px/tile (full detail, small canvas)
// zoom >= 0.5: 2 px/tile (4x fewer pixels, indistinguishable at this zoom)
// zoom <  0.5: 1 px/tile (16x fewer pixels, linear filtering smooths the result)
const computeFogPixelsPerTile = ({ zoom }: { zoom: number }): number => {
  if (zoom >= 1.0) {
    return IS_SAFARI ? 2 : 4;
  }
  if (zoom >= 0.5) {
    return IS_SAFARI ? 1 : 2;
  }
  return 1;
};

// Vision fade: tiles over which visibility transitions from full to zero.
// Wider = softer, more gradual fog boundary.
const VISION_FADE_TILES = 15;

// Square vision uses Chebyshev distance: max(abs(dx), abs(dy))
const VISION_INNER_EXTENT = PAWN_VISION_RADIUS - VISION_FADE_TILES;

// Fog RGBA values (alpha-based darkening with normal blend mode)
const FOG_COLOR_R = 5;
const FOG_COLOR_G = 5;
const FOG_COLOR_B = 12;

// Normalized alpha levels for explored / unexplored regions
const EXPLORED_ALPHA_NORM = 180 / 255;
const UNEXPLORED_ALPHA_NORM = 248 / 255;

// Temporal interpolation speed (higher = faster transitions)
// 3.0 gives ~0.5-1s transitions depending on alpha delta
const FOG_LERP_SPEED = 3.0;

// Convergence threshold: when max|display - target| < this, stop updating.
// 0.01 is imperceptible (~2.5/255 alpha) but cuts the interpolation tail significantly.
const CONVERGENCE_EPSILON = 0.01;

// Sub-tile pawn movement hash: quantize to 0.25 tile for smoother target updates
const PAWN_SUBTILE_QUANT = 4; // 1/0.25
let _lastPawnSubTileHash = 0;

// PERFORMANCE: Preallocated array for pawn tile positions
const _pawnTilePositions: Array<{ x: number; y: number }> = [];

// Track viewport tile bounds for sprite positioning
let _minTileX = 0;
let _minTileY = 0;
let _tilesW = 0;
let _tilesH = 0;

// Canvas pixel dimensions (tilesW/H * _fogPixelsPerTile)
let _canvasW = 0;
let _canvasH = 0;

// Display area: the visible sub-region of the fog canvas (excludes buffer inset)
let _displayMinTileX = 0;
let _displayMinTileY = 0;
let _displayTilesW = 0;
let _displayTilesH = 0;

// ============================================================================
// GPU Fog Filter - GLSL shader for atmospheric fog effects
// ============================================================================
// Post-processes the Canvas2D fog data on the GPU to add:
//   - Edge blur to smooth staircase artifacts from Chebyshev distance
//   - UV distortion with animated FBM noise for organic fog edges
//   - Wispy alpha modulation at fog boundaries
//   - Subtle animated texture in solid fog areas

const FOG_VERTEX_SHADER = `
in vec2 aPosition;
out vec2 vTextureCoord;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

vec4 filterVertexPosition(void) {
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    return vec4(position, 0.0, 1.0);
}

vec2 filterTextureCoord(void) {
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void) {
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
}
`;

const FOG_FRAGMENT_SHADER = `
precision highp float;

in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform vec4 uInputSize;

uniform float uTime;
uniform vec3 uFogColor;
uniform vec2 uWorldOffset;
uniform vec2 uWorldScale;
uniform float uNoiseFrequency;
uniform float uWispIntensity;
uniform float uWispSpeed;
uniform float uDistortStrength;

float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x),
        f.y
    );
}

float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    mat2 rot = mat2(0.877, 0.479, -0.479, 0.877);
    for (int i = 0; i < 4; i++) {
        v += a * vnoise(p);
        p = rot * p * 2.0 + vec2(100.0);
        a *= 0.5;
    }
    return v;
}

void main() {
    vec2 worldCoord = vTextureCoord * uWorldScale + uWorldOffset;
    vec2 nc = worldCoord * uNoiseFrequency;
    float t = uTime * uWispSpeed;

    float n1 = fbm(nc + vec2(t * 0.3, t * 0.1));
    float n2 = fbm(nc * 0.7 + vec2(-t * 0.2, t * 0.25) + vec2(43.2, 17.8));

    // UV distortion breaks up staircase artifacts
    vec2 distort = (vec2(n1, n2) - 0.5) * uDistortStrength * uInputSize.zw;
    vec2 uv = vTextureCoord + distort;

    // 5-tap cross blur for smooth edges
    vec2 ts = uInputSize.zw * 2.5;
    float a0 = texture(uTexture, uv).a;
    float a1 = texture(uTexture, uv + vec2(ts.x, 0.0)).a;
    float a2 = texture(uTexture, uv - vec2(ts.x, 0.0)).a;
    float a3 = texture(uTexture, uv + vec2(0.0, ts.y)).a;
    float a4 = texture(uTexture, uv - vec2(0.0, ts.y)).a;
    float fogAlpha = a0 * 0.4 + (a1 + a2 + a3 + a4) * 0.15;

    if (fogAlpha < 0.005) {
        finalColor = vec4(0.0);
        return;
    }

    float combined = (n1 + n2) * 0.5;

    // Edge mask: fog transition zone
    float edgeMask = smoothstep(0.01, 0.25, fogAlpha)
                   * (1.0 - smoothstep(0.6, 0.95, fogAlpha));

    // Wispy alpha modulation at edges
    float wispEffect = (combined - 0.5) * 2.0 * uWispIntensity * edgeMask;

    // Subtle texture in solid fog
    float solidTexture = (combined - 0.5) * 0.05
                       * smoothstep(0.5, 0.9, fogAlpha);

    float newAlpha = clamp(fogAlpha + wispEffect + solidTexture, 0.0, 1.0);

    float colorVar = combined * 0.03;
    vec3 color = uFogColor + vec3(colorVar * 0.5, colorVar * 0.5, colorVar);

    finalColor = vec4(color * newAlpha, newAlpha);
}
`;

// PERFORMANCE: Direct reference to filter uniforms for fast per-frame updates
let _fogFilter: Filter | null = null;
const _fogWorldOffset = new Float32Array([0, 0]);
const _fogWorldScale = new Float32Array([1, 1]);
const ENABLE_FOG_GPU_FILTER = !IS_SAFARI;

export const createFogLayer = (): Sprite => {
  offscreenCanvas = document.createElement('canvas');
  const context = offscreenCanvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    throw new Error('Cannot get 2d context for fog canvas');
  }
  ctx = context;

  sprite = new Sprite();

  if (ENABLE_FOG_GPU_FILTER) {
    const glProgram = GlProgram.from({
      vertex: FOG_VERTEX_SHADER,
      fragment: FOG_FRAGMENT_SHADER,
      name: 'fog-of-war',
    });

    _fogFilter = new Filter({
      glProgram,
      resources: {
        fogUniforms: {
          uTime: { value: 0, type: 'f32' },
          uFogColor: {
            value: new Float32Array([FOG_COLOR_R / 255, FOG_COLOR_G / 255, FOG_COLOR_B / 255]),
            type: 'vec3<f32>',
          },
          uWorldOffset: { value: _fogWorldOffset, type: 'vec2<f32>' },
          uWorldScale: { value: _fogWorldScale, type: 'vec2<f32>' },
          uNoiseFrequency: { value: 0.08, type: 'f32' },
          uWispIntensity: { value: 0.3, type: 'f32' },
          uWispSpeed: { value: 0.04, type: 'f32' },
          uDistortStrength: { value: 6.0, type: 'f32' },
        },
      },
      padding: 0,
    });

    sprite.filters = [_fogFilter];
  }

  sprite.visible = _fogEnabled;

  return sprite;
};

export const setFogEnabled = ({ enabled }: { enabled: boolean }): void => {
  _fogEnabled = enabled;
  if (sprite) {
    sprite.visible = enabled;
  }
};

// Smoothstep easing for organic-looking fog falloff.
// Maps linear t [0,1] to a smooth S-curve with zero derivative at both ends.
const smoothstep = ({ t }: { t: number }): number => {
  return t * t * (3 - 2 * t);
};

// Compute visibility at a sub-tile world position (0 = not visible, 1 = fully visible).
// Uses Chebyshev distance (max of abs(dx), abs(dy)) for square vision areas.
const computeVisibility = ({ wx, wy }: { wx: number; wy: number }): number => {
  let best = 0;
  for (let i = 0; i < _pawnTilePositions.length; i++) {
    const pawn = _pawnTilePositions[i];
    if (!pawn) {
      continue;
    }
    const adx = Math.abs(wx - pawn.x);
    const ady = Math.abs(wy - pawn.y);
    const chebyshev = Math.max(adx, ady);

    if (chebyshev <= VISION_INNER_EXTENT) {
      return 1; // Fully visible, early return
    }
    if (chebyshev <= PAWN_VISION_RADIUS) {
      const linear = 1 - (chebyshev - VISION_INNER_EXTENT) / VISION_FADE_TILES;
      const curved = smoothstep({ t: linear });
      if (curved > best) {
        best = curved;
      }
    }
  }
  return best;
};

// Bilinear interpolation of explored state across tile boundaries.
// Samples 4 surrounding tiles and blends based on sub-tile position,
// eliminating the hard tile-sized block artifacts at explored/unexplored edges.
const computeExploredAlpha = ({ wx, wy }: { wx: number; wy: number }): number => {
  // Shift by -0.5 so interpolation is centered on tile midpoints
  const sx = wx - 0.5;
  const sy = wy - 0.5;
  const ix = Math.floor(sx);
  const iy = Math.floor(sy);
  const fx = sx - ix;
  const fy = sy - iy;

  // Sample 4 surrounding tiles (1.0 if explored, 0.0 if not)
  const e00 = isTileExplored({ x: ix, y: iy }) ? 1.0 : 0.0;
  const e10 = isTileExplored({ x: ix + 1, y: iy }) ? 1.0 : 0.0;
  const e01 = isTileExplored({ x: ix, y: iy + 1 }) ? 1.0 : 0.0;
  const e11 = isTileExplored({ x: ix + 1, y: iy + 1 }) ? 1.0 : 0.0;

  // Bilinear blend
  const top = e00 + (e10 - e00) * fx;
  const bot = e01 + (e11 - e01) * fx;
  return top + (bot - top) * fy;
};

// Gather pawn positions for fog computation.
// PERFORMANCE: Reuses position objects via indexed access instead of push(),
// avoiding object allocation every frame. Only allocates when pawn count grows.
const gatherPawnPositions = (): void => {
  const pawns = ownedPawns.value;
  let count = 0;
  for (let i = 0; i < pawns.length; i++) {
    const p = pawns[i];
    if (p) {
      const existing = _pawnTilePositions[count];
      if (existing) {
        existing.x = p.x;
        existing.y = p.y;
      } else {
        _pawnTilePositions[count] = { x: p.x, y: p.y };
      }
      count++;
    }
  }
  _pawnTilePositions.length = count;
  updateExploredTiles({ pawnPositions: _pawnTilePositions });
};

// Compute a hash of pawn positions quantized to 0.25-tile resolution.
// Triggers target recomputation on sub-tile movement, not just integer tile crossings.
const computePawnSubTileHash = (): number => {
  let hash = _pawnTilePositions.length;
  for (let i = 0; i < _pawnTilePositions.length; i++) {
    const p = _pawnTilePositions[i];
    if (p) {
      // Quantize to 0.25 tile increments
      const qx = Math.round(p.x * PAWN_SUBTILE_QUANT);
      const qy = Math.round(p.y * PAWN_SUBTILE_QUANT);
      // Simple hash combine -- collisions are fine, just triggers a recompute
      hash = (hash * 31 + qx) | 0;
      hash = (hash * 31 + qy) | 0;
    }
  }
  return hash;
};

// Check if fog target needs recomputation, update cache if so
const needsTargetRecompute = ({
  cameraTileX,
  cameraTileY,
  zoom,
  screenWidth,
  screenHeight,
  pawnSubTileHash,
}: {
  cameraTileX: number;
  cameraTileY: number;
  zoom: number;
  screenWidth: number;
  screenHeight: number;
  pawnSubTileHash: number;
}): boolean => {
  const currentFogVersion = fogVersion.value;
  if (
    currentFogVersion === _lastFogVersion &&
    cameraTileX === _lastCameraTileX &&
    cameraTileY === _lastCameraTileY &&
    zoom === _lastZoom &&
    screenWidth === _lastScreenW &&
    screenHeight === _lastScreenH &&
    pawnSubTileHash === _lastPawnSubTileHash
  ) {
    return false;
  }
  _lastFogVersion = currentFogVersion;
  _lastCameraTileX = cameraTileX;
  _lastCameraTileY = cameraTileY;
  _lastZoom = zoom;
  _lastScreenW = screenWidth;
  _lastScreenH = screenHeight;
  _lastPawnSubTileHash = pawnSubTileHash;
  return true;
};

// Calculate viewport tile bounds with margin
const computeViewportTileBounds = ({
  camera,
  screenWidth,
  screenHeight,
}: {
  camera: { readonly x: number; readonly zoom: number; readonly y: number };
  screenWidth: number;
  screenHeight: number;
}): void => {
  const halfW = screenWidth / 2 / camera.zoom / TILE_SIZE;
  const halfH = screenHeight / 2 / camera.zoom / TILE_SIZE;
  const centerTileX = camera.x / TILE_SIZE;
  const centerTileY = camera.y / TILE_SIZE;

  _minTileX = Math.floor(centerTileX - halfW) - FOG_MARGIN_TILES;
  const maxTileX = Math.ceil(centerTileX + halfW) + FOG_MARGIN_TILES;
  _minTileY = Math.floor(centerTileY - halfH) - FOG_MARGIN_TILES;
  const maxTileY = Math.ceil(centerTileY + halfH) + FOG_MARGIN_TILES;

  _tilesW = maxTileX - _minTileX;
  _tilesH = maxTileY - _minTileY;
  _canvasW = _tilesW * _fogPixelsPerTile;
  _canvasH = _tilesH * _fogPixelsPerTile;

  // Display area: inset from the full canvas to hide buffer zone
  _displayMinTileX = _minTileX + FOG_DISPLAY_INSET_TILES;
  _displayMinTileY = _minTileY + FOG_DISPLAY_INSET_TILES;
  _displayTilesW = _tilesW - 2 * FOG_DISPLAY_INSET_TILES;
  _displayTilesH = _tilesH - 2 * FOG_DISPLAY_INSET_TILES;
};

// Ensure canvas, ImageData, and Float32 buffers are sized correctly.
// Returns true if canvas size changed (requires full re-init of display buffer).
const ensureBuffers = (): boolean => {
  const totalPixels = _canvasW * _canvasH;
  let sizeChanged = false;

  if (offscreenCanvas.width !== _canvasW || offscreenCanvas.height !== _canvasH) {
    offscreenCanvas.width = _canvasW;
    offscreenCanvas.height = _canvasH;
    _imageData = null;
    sizeChanged = true;
  }
  if (!_imageData || _imageData.width !== _canvasW || _imageData.height !== _canvasH) {
    _imageData = ctx.createImageData(_canvasW, _canvasH);
  }
  if (_targetAlpha.length !== totalPixels) {
    _targetAlpha = new Float32Array(totalPixels);
    sizeChanged = true;
  }
  if (_displayAlpha.length !== totalPixels) {
    _displayAlpha = new Float32Array(totalPixels);
    // Fill display with unexplored alpha so new pixels start dark
    _displayAlpha.fill(UNEXPLORED_ALPHA_NORM);
    sizeChanged = true;
  }
  if (_remapTemp.length !== totalPixels) {
    _remapTemp = new Float32Array(totalPixels);
  }

  return sizeChanged;
};

// Compute target alpha for each fog pixel based on visibility + explored state.
// Writes normalized 0..1 values to _targetAlpha.
const computeTargetAlpha = (): void => {
  const totalPixels = _canvasW * _canvasH;
  if (_targetAlpha.length !== totalPixels) {
    return;
  }

  for (let py = 0; py < _canvasH; py++) {
    const wy = _minTileY + (py + 0.5) * _invFogPixelsPerTile;
    for (let px = 0; px < _canvasW; px++) {
      const wx = _minTileX + (px + 0.5) * _invFogPixelsPerTile;
      const idx = py * _canvasW + px;
      const visibility = computeVisibility({ wx, wy });

      if (visibility >= 0.99) {
        // Fully visible: transparent
        _targetAlpha[idx] = 0;
      } else if (visibility > 0.01) {
        // Partially visible (fade zone): interpolate alpha
        _targetAlpha[idx] = EXPLORED_ALPHA_NORM * (1 - visibility);
      } else {
        // Not visible: use bilinear explored interpolation for smooth boundary
        const exploredBlend = computeExploredAlpha({ wx, wy });
        // Blend between unexplored alpha and explored alpha based on explored state
        _targetAlpha[idx] =
          UNEXPLORED_ALPHA_NORM + (EXPLORED_ALPHA_NORM - UNEXPLORED_ALPHA_NORM) * exploredBlend;
      }
    }
  }
};

// Copy display from target (used when canvas is first created or resized)
const initDisplayFromTarget = (): void => {
  for (let i = 0; i < _targetAlpha.length; i++) {
    _displayAlpha[i] = _targetAlpha[i] as number;
  }
};

// Shift display pixels by (dxPx, dyPx) using _remapTemp as scratch.
// Pixels that fall outside the old canvas are initialized from _targetAlpha.
const shiftDisplayPixels = ({ dxPx, dyPx }: { dxPx: number; dyPx: number }): void => {
  // PERFORMANCE: fill with sentinel to detect unmapped pixels
  _remapTemp.fill(-1);

  for (let ny = 0; ny < _canvasH; ny++) {
    const oy = ny - dyPx;
    if (oy < 0 || oy >= _canvasH) {
      continue;
    }
    for (let nx = 0; nx < _canvasW; nx++) {
      const ox = nx - dxPx;
      if (ox < 0 || ox >= _canvasW) {
        continue;
      }
      _remapTemp[ny * _canvasW + nx] = _displayAlpha[oy * _canvasW + ox] as number;
    }
  }

  // Write back: remapped pixels keep their interpolated value,
  // unmapped pixels initialize from target
  for (let i = 0; i < _remapTemp.length; i++) {
    const val = _remapTemp[i] as number;
    _displayAlpha[i] = val >= 0 ? val : (_targetAlpha[i] as number);
  }
};

// Update bookkeeping for previous viewport state
const updatePrevViewport = (): void => {
  _prevMinTileX = _minTileX;
  _prevMinTileY = _minTileY;
  _prevCanvasW = _canvasW;
  _prevCanvasH = _canvasH;
};

// When viewport origin shifts (camera movement), copy overlapping display pixels
// to their new positions. New pixels that have no overlap source initialize from target.
// This prevents fog from "jumping" when camera tile bounds change.
const remapDisplayBuffer = (): void => {
  // If no previous data or canvas size changed, initialize display from target
  if (_prevMinTileX === -99999 || _prevCanvasW !== _canvasW || _prevCanvasH !== _canvasH) {
    initDisplayFromTarget();
    updatePrevViewport();
    return;
  }

  // Pixel offset between old and new viewport origins
  const dxPx = (_prevMinTileX - _minTileX) * _fogPixelsPerTile;
  const dyPx = (_prevMinTileY - _minTileY) * _fogPixelsPerTile;

  if (dxPx !== 0 || dyPx !== 0) {
    shiftDisplayPixels({ dxPx, dyPx });
  }

  updatePrevViewport();
};

// Per-frame exponential lerp of display toward target.
// Frame-rate independent: display += (target - display) * (1 - exp(-speed * dt))
// Returns true if any pixel is still converging.
const interpolateDisplayAlpha = ({ dt }: { dt: number }): boolean => {
  const factor = 1 - Math.exp(-FOG_LERP_SPEED * dt);
  let maxDelta = 0;
  const len = _displayAlpha.length;

  for (let i = 0; i < len; i++) {
    const target = _targetAlpha[i] as number;
    const current = _displayAlpha[i] as number;
    const delta = target - current;
    const absDelta = delta > 0 ? delta : -delta;

    if (absDelta > CONVERGENCE_EPSILON) {
      _displayAlpha[i] = current + delta * factor;
      if (absDelta > maxDelta) {
        maxDelta = absDelta;
      }
    } else {
      // Snap to target when close enough
      _displayAlpha[i] = target;
    }
  }

  return maxDelta > CONVERGENCE_EPSILON;
};

// Convert _displayAlpha to RGBA ImageData and put it on the canvas
const writeDisplayToImageData = (): void => {
  if (!_imageData) {
    return;
  }
  const data = _imageData.data;
  const len = _displayAlpha.length;

  for (let i = 0; i < len; i++) {
    const alpha = _displayAlpha[i] as number;
    const pixelIdx = i * 4;

    if (alpha < 0.004) {
      // Nearly transparent: zero out
      data[pixelIdx] = 0;
      data[pixelIdx + 1] = 0;
      data[pixelIdx + 2] = 0;
      data[pixelIdx + 3] = 0;
    } else {
      data[pixelIdx] = FOG_COLOR_R;
      data[pixelIdx + 1] = FOG_COLOR_G;
      data[pixelIdx + 2] = FOG_COLOR_B;
      data[pixelIdx + 3] = Math.round(alpha * 255);
    }
  }

  ctx.putImageData(_imageData, 0, 0);
};

// Upload fog texture to GPU. Reuses existing texture when canvas size unchanged;
// only destroys/recreates when canvas dimensions change.
const uploadFogTexture = ({ forceRecreate }: { forceRecreate: boolean }): void => {
  if (_currentTexture && !forceRecreate) {
    // PERFORMANCE: update in-place instead of destroy/recreate
    _currentTexture.source.update();
    return;
  }

  if (_currentTexture) {
    _currentTexture.destroy(true);
    _currentTexture = null;
  }
  _currentTexture = Texture.from({
    resource: offscreenCanvas,
    alphaMode: 'premultiply-alpha-on-upload',
  });
  _currentTexture.source.scaleMode = 'linear';

  // Crop texture to display area, hiding the buffer inset zone.
  // Mutate the existing frame rectangle in place (frame property is readonly).
  _currentTexture.frame.x = _fogDisplayInsetPx;
  _currentTexture.frame.y = _fogDisplayInsetPx;
  _currentTexture.frame.width = _canvasW - 2 * _fogDisplayInsetPx;
  _currentTexture.frame.height = _canvasH - 2 * _fogDisplayInsetPx;
  _currentTexture.updateUvs();

  sprite.texture = _currentTexture;
};

// Position and scale the sprite in screen space so the canvas
// maps precisely to the visible tile area.
const updateSpriteTransform = ({
  camera,
  screenWidth,
  screenHeight,
}: {
  camera: { readonly x: number; readonly y: number; readonly zoom: number };
  screenWidth: number;
  screenHeight: number;
}): void => {
  const tileScreenSize = TILE_SIZE * camera.zoom;
  sprite.x = (_displayMinTileX * TILE_SIZE - camera.x) * camera.zoom + screenWidth / 2;
  sprite.y = (_displayMinTileY * TILE_SIZE - camera.y) * camera.zoom + screenHeight / 2;
  sprite.width = _displayTilesW * tileScreenSize;
  sprite.height = _displayTilesH * tileScreenSize;
};

// Update fog filter uniforms every frame for animation
const updateFogFilterUniforms = (): void => {
  if (!_fogFilter) {
    return;
  }
  const uniformGroup = _fogFilter.resources['fogUniforms'] as { uniforms: Record<string, unknown> };
  uniformGroup.uniforms['uTime'] = performance.now() * 0.001;
  _fogWorldOffset[0] = _displayMinTileX;
  _fogWorldOffset[1] = _displayMinTileY;
  _fogWorldScale[0] = _displayTilesW;
  _fogWorldScale[1] = _displayTilesH;
};

export const drawFog = (): void => {
  if (!_fogEnabled) {
    if (sprite) {
      sprite.visible = false;
    }
    return;
  }

  // Frame timing
  const now = performance.now();
  const dt = _lastFrameTime === 0 ? 0.016 : Math.min((now - _lastFrameTime) * 0.001, 0.1);
  _lastFrameTime = now;

  gatherPawnPositions();

  const camera = getCamera();
  const { width: screenWidth, height: screenHeight } = getScreenDimensions();
  const cameraTileX = Math.floor(camera.x / TILE_SIZE);
  const cameraTileY = Math.floor(camera.y / TILE_SIZE);
  const pawnSubTileHash = computePawnSubTileHash();

  // PERFORMANCE: Adjust fog resolution based on zoom level.
  // At low zoom, each tile covers fewer screen pixels so sub-tile detail is wasted.
  const newPpt = computeFogPixelsPerTile({ zoom: camera.zoom });
  if (newPpt !== _fogPixelsPerTile) {
    _fogPixelsPerTile = newPpt;
    _invFogPixelsPerTile = 1 / _fogPixelsPerTile;
    _fogDisplayInsetPx = FOG_DISPLAY_INSET_TILES * _fogPixelsPerTile;
  }

  const targetChanged = needsTargetRecompute({
    cameraTileX,
    cameraTileY,
    zoom: camera.zoom,
    screenWidth,
    screenHeight,
    pawnSubTileHash,
  });

  if (targetChanged) {
    computeViewportTileBounds({ camera, screenWidth, screenHeight });
    const sizeChanged = ensureBuffers();
    computeTargetAlpha();
    remapDisplayBuffer();
    _isConverged = false;

    if (sizeChanged) {
      uploadFogTexture({ forceRecreate: true });
    }
  }

  if (!_isConverged) {
    const stillConverging = interpolateDisplayAlpha({ dt });
    writeDisplayToImageData();
    uploadFogTexture({ forceRecreate: false });

    if (!stillConverging) {
      _isConverged = true;
    }
  }

  updateSpriteTransform({ camera, screenWidth, screenHeight });
  updateFogFilterUniforms();
};
